import { useEffect, useRef, useState } from 'react';
import CookieConsent from './components/CookieConsent';
import MapComponent from './components/MapComponent';
import Navbar from './components/Navbar';
import SearchForm from './components/SearchForm';
import SearchResultsList from './components/SearchResultsList';
import apiService from './services/api';
import './styles/App.css';
import { CareHome, SearchRequest } from './types';
import { clearAllCookies, initializeUser, setUserData, UserData } from './utils/cookieUtils';
import { disableGA, initializeGA, setUserProperties, trackCareHomeSelection, trackSearch } from './utils/googleAnalytics';

function App() {
    const [careHomes, setCareHomes] = useState<CareHome[]>([]);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 51.5074, lng: -0.1278 });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isSearchFormCollapsed, setIsSearchFormCollapsed] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState<boolean>(false);
    const [selectedCareHome, setSelectedCareHome] = useState<CareHome | null>(null);
    const [showCookieConsent, setShowCookieConsent] = useState<boolean>(false);
    const [userData, setUserDataState] = useState<UserData | null>(null);
    const [closeInfoWindowTrigger, setCloseInfoWindowTrigger] = useState<number>(0);
    const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
    const [lastSearchPostcode, setLastSearchPostcode] = useState<string>('');
    const [mapZoom, setMapZoom] = useState<number>(12);
    const [externalPostcode, setExternalPostcode] = useState<string | undefined>(undefined);
    const [initialInfoWindowCareHome, setInitialInfoWindowCareHome] = useState<CareHome | null>(null);
    const carehomeParamHandled = useRef(false);
    
    // Debug selectedCareHome state changes
    useEffect(() => {
        console.log('🏠 App: selectedCareHome state changed to:', selectedCareHome ? selectedCareHome.organisation_name : 'null');
    }, [selectedCareHome]);

    // Initialize user and check cookie consent on app load
    useEffect(() => {
        const initialUserData = initializeUser();
        setUserDataState(initialUserData);
        
        // Show cookie consent if not already given
        if (!initialUserData.consentGiven) {
            setShowCookieConsent(true);
        } else {
            // Initialize Google Analytics for returning users with consent
            initializeGA();
            setUserProperties(initialUserData.userId);
            console.log('🔍 App: Google Analytics initialized for returning user');
        }
        
        console.log('🍪 App: User initialized:', {
            userId: initialUserData.userId,
            consentGiven: initialUserData.consentGiven,
            hasLastSearch: !!initialUserData.lastSearch
        });
    }, []);

    // Handle responsive layout
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 768);
        };

        // Set initial value
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle ?carehome= URL parameter for deep-link search (update 2026-03-01.2)
    useEffect(() => {
        if (carehomeParamHandled.current) return;
        const params = new URLSearchParams(window.location.search);
        const carehomeId = params.get('carehome');
        if (!carehomeId) return;

        carehomeParamHandled.current = true;
        setLoading(true);

        apiService.searchByCareHomeId(carehomeId)
            .then(response => {
                if (response.success && response.data.length > 0) {
                    setCareHomes(response.data);
                    setSearchPerformed(true);

                    const target = response.data.find(h => h.reg_id === carehomeId);
                    if (target) {
                        setMapCenter({ lat: target.latitude, lng: target.longitude });
                        setSelectedCareHome(target);
                        setInitialInfoWindowCareHome(target);
                        setExternalPostcode(target.post_code);
                        setLastSearchPostcode(target.post_code);
                    } else {
                        setMapCenter({ lat: response.searchCenter.latitude, lng: response.searchCenter.longitude });
                        setExternalPostcode(response.searchCenter.postcode);
                        setLastSearchPostcode(response.searchCenter.postcode);
                    }

                    // Zoom to ~2x the search radius
                    const radius = response.radiusMiles || 5;
                    const zoom = Math.round(14 - Math.log2(radius * 2));
                    setMapZoom(Math.max(8, Math.min(18, zoom)));

                    setIsSearchFormCollapsed(true);
                }
            })
            .catch(err => {
                console.error('Carehome deep-link search failed:', err);
                setError(err instanceof Error ? err.message : 'Failed to load care home');
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSelectCareHome = (careHome: CareHome) => {
        console.log('🎯 App: Care home selected:', careHome.organisation_name, 'ID:', careHome.id, 'Type:', typeof careHome.id);
        
        // Close any open InfoWindow when a search result is selected
        setCloseInfoWindowTrigger(prev => prev + 1);
        
        // Center map on selected care home
        setMapCenter({
            lat: careHome.latitude,
            lng: careHome.longitude
        });
        // Set selected care home for pin highlighting
        setSelectedCareHome(careHome);
        
        // Track care home selection in Google Analytics (only if consent given)
        if (userData?.consentGiven) {
            trackCareHomeSelection(careHome.organisation_name, 'search_card');
        }
        
        console.log('🎯 App: selectedCareHome state updated to:', {
            id: careHome.id,
            name: careHome.organisation_name,
            idType: typeof careHome.id
        });
    };

    const handleToggleSearchFormCollapse = () => {
        setIsSearchFormCollapsed(!isSearchFormCollapsed);
    };

    const handleCookieAccept = () => {
        if (userData) {
            const updatedUserData: UserData = {
                ...userData,
                consentGiven: true,
                consentDate: new Date().toISOString()
            };
            
            setUserData(updatedUserData);
            setUserDataState(updatedUserData);
            setShowCookieConsent(false);
            
            // Initialize Google Analytics when consent is given
            initializeGA();
            setUserProperties(updatedUserData.userId);
            
            console.log('🍪 App: Cookie consent accepted for user:', updatedUserData.userId);
            console.log('🔍 App: Google Analytics initialized');
        }
    };

    const handleCookieReject = () => {
        // Clear any existing cookies
        clearAllCookies();
        // Disable Google Analytics
        disableGA();
        // Redirect will be handled by the CookieConsent component
        console.log('🍪 App: Cookie consent rejected, redirecting to OpenScore.org.uk');
        console.log('🔍 App: Google Analytics disabled');
    };

    const handleSearch = async (
        postcode: string, 
        radius: number, 
        residentialCare: boolean, 
        dementiaCare: boolean, 
        nursingCare: boolean, 
        sort: 'score' | 'fee_low' | 'fee_high',
        fundingType: 'all' | 'self_funding' | 'la_funding' | 'la+topup_funding' | 'chc_funding',
        eventFilter: string = '-1'
    ) => {
        setLoading(true);
        setError(null);
        setLastSearchPostcode(postcode);  // Save postcode for action tracking
        
        try {
            const searchRequest: SearchRequest = {
                browser_session: 'web_session_' + Date.now(),
                user_id: userData?.consentGiven ? userData.userId : 'anonymous_' + Date.now(),
                starting_post_code: postcode,
                miles_radius: radius,
                residential_needs: residentialCare,
                dementia_needs: dementiaCare,
                nursing_needs: nursingCare,
                sort: sort,
                fundingType: fundingType,
                event_filter: eventFilter
            };

            const response = await apiService.searchCareHomes(searchRequest);
            
            // Track search in Google Analytics (only if consent given)
            if (userData?.consentGiven) {
                trackSearch(postcode, radius, {
                    residential_needs: residentialCare,
                    dementia_needs: dementiaCare,
                    nursing_needs: nursingCare,
                    sort: sort
                });
            }
            
            if (response.success) {
                // Debug: Check for duplicate IDs in the response
                const ids = response.data.map(ch => ch.id);
                const uniqueIds = new Set(ids);
                if (ids.length !== uniqueIds.size) {
                    console.warn('⚠️ App: Duplicate IDs found in care homes data!', {
                        totalCareHomes: ids.length,
                        uniqueIds: uniqueIds.size,
                        duplicates: ids.filter((id, index) => ids.indexOf(id) !== index)
                    });
                }
                
                console.log('🏠 App: Loaded care homes:', response.data.length, 'homes with IDs:', ids);
                
                setCareHomes(response.data);
                setMapCenter({
                    lat: response.searchCenter.latitude,
                    lng: response.searchCenter.longitude
                });
                // Mark that a search has been performed
                setSearchPerformed(true);
                // Clear any previous selection when new search is performed
                setSelectedCareHome(null);
                // Auto-collapse form when results are found
                if (response.data.length > 0) {
                    setIsSearchFormCollapsed(true);
                }
            } else {
                setError('Search failed. Please try again.');
                setSearchPerformed(true);
            }
        } catch (err) {
            console.error('Search error:', err);
            setError(err instanceof Error ? err.message : 'An unexpected error occurred');
            setSearchPerformed(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="app">
                {/* Desktop: Sidebar with search form */}
                {!isMobileView && (
                    <div className="sidebar">
                        <SearchForm 
                            onSearch={handleSearch} 
                            loading={loading} 
                            hasResults={careHomes.length > 0}
                            isCollapsed={isSearchFormCollapsed}
                            onToggleCollapse={handleToggleSearchFormCollapse}
                            resultsCount={careHomes.length}
                            searchPerformed={searchPerformed}
                            externalPostcode={externalPostcode}
                        />
                        
                        {error && (
                            <div className="error-message">
                                <p>{error}</p>
                            </div>
                        )}
                        
                        {/* Desktop: Show results list in sidebar */}
                        {careHomes.length > 0 && (
                            <SearchResultsList 
                                careHomes={careHomes}
                                onSelectCareHome={handleSelectCareHome}
                                userId={userData?.userId || ''}
                                startingPostCode={lastSearchPostcode}
                            />
                        )}
                    </div>
                )}
                
                {/* Mobile: Search form overlay when needed */}
                {isMobileView && (
                    <SearchForm 
                        onSearch={handleSearch} 
                        loading={loading} 
                        hasResults={careHomes.length > 0}
                        isCollapsed={isSearchFormCollapsed}
                        onToggleCollapse={handleToggleSearchFormCollapse}
                        resultsCount={careHomes.length}
                        searchPerformed={searchPerformed}
                        externalPostcode={externalPostcode}
                    />
                )}
                
                {/* Mobile: Error messages */}
                {isMobileView && error && (
                    <div className="error-message mobile-error">
                        <p>{error}</p>
                    </div>
                )}
                
                <div className="map-container">
                    <MapComponent 
                        careHomes={careHomes}
                        center={mapCenter}
                        zoom={mapZoom}
                        selectedCareHome={selectedCareHome}
                        closeInfoWindow={closeInfoWindowTrigger}
                        initialInfoWindowCareHome={initialInfoWindowCareHome}
                        userId={userData?.userId || ''}
                        startingPostCode={lastSearchPostcode}
                    />
                </div>
            </div>
            
            {/* Mobile: Show results list as horizontal strip overlay */}
            {isMobileView && careHomes.length > 0 && (
                <SearchResultsList 
                    careHomes={careHomes}
                    onSelectCareHome={handleSelectCareHome}
                    userId={userData?.userId || ''}
                    startingPostCode={lastSearchPostcode}
                />
            )}
            
            {/* Cookie Consent Modal */}
            {showCookieConsent && (
                <CookieConsent 
                    onAccept={handleCookieAccept}
                    onReject={handleCookieReject}
                />
            )}
        </>
    );
}

export default App; 